import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BarChart3,
  Users,
  BookOpen,
  Trophy,
  Target,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Award,
  Sparkles,
  RefreshCw,
  Loader2,
  Brain,
  Lightbulb,
  ArrowUp,
  ArrowDown,
  GraduationCap,
  Medal,
  Download,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LearningAnalytics = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { branding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/analytics/team`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/analytics/team/export-xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-T:]/g, '');
      a.download = `team_progress_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Team progress report downloaded');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.response?.status === 403
        ? 'You do not have permission to export team data'
        : 'Failed to export team progress'
      );
    } finally {
      setExporting(false);
    }
  };

  const fetchAIInsights = async () => {
    setLoadingInsights(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/ai/learning-insights`, { headers });
      setInsights(response.data.insights);
      toast.success('AI insights generated');
    } catch (error) {
      console.error('Failed to fetch AI insights:', error);
      toast.error('Failed to generate insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500 bg-red-500/10';
      case 'medium': return 'text-amber-500 bg-amber-500/10';
      case 'low': return 'text-green-500 bg-green-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-amber-500/10 text-amber-500',
      training_team: 'bg-purple-500/10 text-purple-500',
      regional_manager: 'bg-blue-500/10 text-blue-500',
      store_manager: 'bg-green-500/10 text-green-500',
      cra: 'bg-gray-500/10 text-gray-500',
    };
    return colors[role] || colors.cra;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="learning-analytics">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <BarChart3 className="w-8 h-8" style={{ color: branding.primary_color }} />
              {t('analytics.title', 'Learning Analytics')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('analytics.subtitle', 'Track team progress, performance, and AI-powered insights')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchAnalytics} data-testid="analytics-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('common.refresh', 'Refresh')}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={exporting}
              data-testid="analytics-export-excel-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {t('analytics.exportExcel', 'Export Excel')}
            </Button>
            <Button onClick={fetchAIInsights} disabled={loadingInsights} data-testid="analytics-ai-insights-btn">
              {loadingInsights ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Brain className="w-4 h-4 mr-2" />
              )}
              {t('analytics.aiInsights', 'AI Insights')}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${branding.primary_color}20` }}>
                  <Users className="w-5 h-5" style={{ color: branding.primary_color }} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.summary?.total_users || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Learners</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.summary?.completion_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Completion Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Target className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.summary?.avg_quiz_score || 0}%</p>
                  <p className="text-xs text-muted-foreground">Avg Quiz Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <BookOpen className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.summary?.total_enrollments || 0}</p>
                  <p className="text-xs text-muted-foreground">Enrollments</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Insights Section */}
        {insights && insights.length > 0 && (
          <Card className="border-l-4" style={{ borderLeftColor: branding.primary_color }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: branding.primary_color }} />
                AI-Powered Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {insights.map((insight, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${getPriorityColor(insight.priority)}`}
                  >
                    <div className="flex items-start gap-3">
                      <Lightbulb className="w-5 h-5 mt-0.5" />
                      <div>
                        <h4 className="font-semibold mb-1">{insight.title}</h4>
                        <p className="text-sm opacity-90">{insight.insight}</p>
                        <Badge variant="outline" className="mt-2 capitalize">
                          {insight.priority} priority
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="performers">
              <Trophy className="w-4 h-4 mr-2" />
              Top Performers
            </TabsTrigger>
            <TabsTrigger value="attention">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Needs Attention
            </TabsTrigger>
            <TabsTrigger value="courses">
              <BookOpen className="w-4 h-4 mr-2" />
              Courses
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Role Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Performance by Role</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics?.role_breakdown && Object.entries(analytics.role_breakdown).map(([role, data]) => (
                      <div key={role} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge className={getRoleColor(role)} variant="outline">
                            {role.replace('_', ' ')}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {data.count} users
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-amber-500" />
                          <span className="font-semibold">{data.avg_points} avg pts</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Enrollment Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Enrollment Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm">Completed</span>
                        <span className="font-semibold text-green-500">
                          {analytics?.summary?.completed_enrollments || 0}
                        </span>
                      </div>
                      <Progress 
                        value={(analytics?.summary?.completed_enrollments / analytics?.summary?.total_enrollments) * 100 || 0} 
                        className="h-2"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm">In Progress</span>
                        <span className="font-semibold text-amber-500">
                          {analytics?.summary?.in_progress_enrollments || 0}
                        </span>
                      </div>
                      <Progress 
                        value={(analytics?.summary?.in_progress_enrollments / analytics?.summary?.total_enrollments) * 100 || 0}
                        className="h-2 [&>div]:bg-amber-500"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Top Performers Tab */}
          <TabsContent value="performers" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Top Performers
                </CardTitle>
                <CardDescription>
                  Learners with the highest points and achievements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analytics?.top_performers?.map((performer, index) => (
                    <div
                      key={performer.id}
                      className={`flex items-center gap-4 p-4 rounded-lg ${
                        index < 3 ? 'bg-gradient-to-r from-amber-500/10 to-transparent' : 'bg-muted/30'
                      }`}
                      data-testid={`performer-${performer.id}`}
                    >
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-amber-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-amber-700 text-white' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {index < 3 ? <Medal className="w-5 h-5" /> : index + 1}
                      </div>
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={performer.avatar} />
                        <AvatarFallback>{performer.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-semibold">{performer.name}</p>
                        <p className="text-sm text-muted-foreground">{performer.email}</p>
                      </div>
                      <Badge className={getRoleColor(performer.role)} variant="outline">
                        {performer.role?.replace('_', ' ')}
                      </Badge>
                      <div className="text-right">
                        <p className="font-bold text-amber-500">{performer.points || 0}</p>
                        <p className="text-xs text-muted-foreground">points</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Needs Attention Tab */}
          <TabsContent value="attention" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Users Needing Support
                </CardTitle>
                <CardDescription>
                  Learners with stalled progress who may need assistance
                </CardDescription>
              </CardHeader>
              <CardContent>
                {analytics?.users_needing_attention?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Stalled Courses</TableHead>
                        <TableHead>Avg Progress</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.users_needing_attention.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getRoleColor(user.role)} variant="outline">
                              {user.role?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-red-500 font-semibold">{user.stalled_courses}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={user.total_progress} className="w-24 h-2" />
                              <span className="text-sm">{user.total_progress}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button variant="outline" size="sm">
                              Send Reminder
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>All learners are making good progress!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" style={{ color: branding.primary_color }} />
                  Course Performance
                </CardTitle>
                <CardDescription>
                  Enrollment and completion rates by course
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Course</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Enrollments</TableHead>
                      <TableHead>Completion Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics?.popular_courses?.map((course) => (
                      <TableRow key={course.id}>
                        <TableCell>
                          <p className="font-medium">{course.title}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{course.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            {course.enrollments}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={course.completion_rate} className="w-24 h-2" />
                            <span className={`text-sm ${
                              course.completion_rate >= 70 ? 'text-green-500' :
                              course.completion_rate >= 40 ? 'text-amber-500' :
                              'text-red-500'
                            }`}>
                              {course.completion_rate}%
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default LearningAnalytics;
